import { AcimaLauncher } from './launcher'

let darkBlue = '#050044'
let orange = '#f75200'
let darkGray = '#696871'
let lightGray = '#dcdcdc'
let font = '\'Nunito Sans\', sans-serif'

export class Modal {
  private _parentElem: HTMLElement
  private _modalElement: HTMLDivElement | null = null
  private _buttonDiv: HTMLElement | null = null
  private _merchantId: string
  private _iframeUrl: string
  private enableCTA: boolean
  private zIndex: string = '1000'

  /**
   * Creates the acima modal.
   * 
   * @param buttonId The element to add the button to.
   * @param merchantId The merchant ID.
   * @param iframeUrl The URL of the iframe.
   * @param enableCTA Enables the CTA button in the modal. Defaults to true. 
   * @param extraConfig Extra, optional configuration options for the modal.
   */
  constructor(buttonId: string, merchantId: string, iframeUrl: string, enableCTA: boolean, extraConfig: ModalExtraConfig = {}) {
    this._merchantId = merchantId
    this._iframeUrl = iframeUrl
    this.enableCTA = enableCTA

    // Extract extra config
    this.zIndex = extraConfig?.zIndex || this.zIndex

    this._parentElem = document.body
    this._buttonDiv = document.getElementById(buttonId)

    let font1 = document.createElement('link')
    font1.setAttribute('rel', 'preconnect')
    font1.setAttribute('href', 'https://fonts.googleapis.com')


    let font2 = document.createElement('link')
    font2.setAttribute('rel', 'preconnect')
    font2.setAttribute('href', 'https://fonts.gstatic.com')
    font2.setAttribute('crossorigin', '')

    let font3 = document.createElement('link')
    font3.setAttribute('rel', 'stylesheet')
    font3.setAttribute('href', 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@400;800&display=swap')

    let hd = document.getElementsByTagName('head')[0]
    hd?.appendChild(font1)
    hd?.appendChild(font2)
    hd?.appendChild(font3)


    let button = document.createElement('img')
    button.src = `${iframeUrl}/assets/learnMore.svg`
    button.addEventListener('click', this.showModal)

    this._buttonDiv?.appendChild(button)
  }

  createModal() {
    this._modalElement = document.createElement('div')
    this._modalElement.setAttribute('allowtransparency', 'true')
    this._modalElement.style.zIndex = this.zIndex
    this._modalElement.style.width = '100%'
    this._modalElement.style.height = '100%'
    this._modalElement.style.border = 'none'
    this._modalElement.style.position = 'fixed'
    this._modalElement.style.top = '0'
    this._modalElement.style.left = '0'
    this._modalElement.style.backgroundColor = 'rgba(70, 68, 68, 0.5)'
    this._modalElement.style.display = 'flex'
    this._modalElement.style.boxSizing = 'border-box'
    this._modalElement.style.flexDirection = 'column'
    const modalClassName = 'acima-marketing-modal-responsive-padding'
    this._modalElement.className = modalClassName

    const modalStyle = document.createElement('style');
    modalStyle.innerHTML = `
.${modalClassName} { padding: 80px; }

#explanation-container {
  display: flex;
}

@media (max-width: 900px) {
    .${modalClassName} { padding: 2.5rem; }
}

@media (max-width: 768px) {
  .${modalClassName} { padding: 1rem; }

  #explanation-container {
    display: block;
  }

  #explanation-container > * {
    margin: 1em 0;
  }
}
`

    this._modalElement.appendChild(this.modalContent())
    this._modalElement.appendChild(modalStyle)

    this._parentElem.appendChild(this._modalElement)
  }

  showModal = () => {
    if (this._modalElement) {
      this._modalElement.style.display = 'flex'
    }
    else {
      this.createModal()
    }
  }

  hideModal() {
    if (this._modalElement) {
      this._modalElement.style.display = 'none'
    }
    else {
      console.error('Modal already closed')
    }
  }

  modalContent(): HTMLElement {
    let rec = document.createElement('div')
    rec.style.maxWidth = '850px'
    rec.style.maxHeight = 'auto'
    rec.style.alignSelf = 'center'
    rec.style.borderRadius = '10px'
    rec.style.margin = 'auto'
    rec.style.backgroundColor = 'white'
    rec.style.padding = '20px'
    rec.style.fontFamily = font
    rec.style.display = 'flex'
    rec.style.flexDirection = 'column'
    rec.style.overflow = 'hidden'

    rec.appendChild(this.closeButton())

    let scrollable = document.createElement('div')
    scrollable.style.overflow = 'auto';
    scrollable.style.marginTop = '0.5rem';
    scrollable.appendChild(this.acimaLogo())
    scrollable.appendChild(this.headline())
    scrollable.appendChild(this.byline())
    scrollable.appendChild(this.leasingExplanation())
    if (this.enableCTA) {
      scrollable.appendChild(this.callToAction())
    }
    scrollable.appendChild(this.caveat())
    rec.appendChild(scrollable)

    return rec
  }

  closeButton(): HTMLElement {
    const closeButton = document.createElement('div')
    closeButton.style.fontSize = '1.5rem'
    closeButton.style.fontWeight = '700'
    closeButton.style.lineHeight = '1'
    closeButton.style.height = '24px'

    let closeX = document.createElement('span')
    closeX.textContent = 'X'
    closeX.style.float = 'right'
    closeX.addEventListener('click', () => this.hideModal())

    closeButton.appendChild(closeX)
    return closeButton
  }

  acimaLogo(): HTMLElement {
    const logoContainer = document.createElement('div')
    let logoImage = document.createElement('img')
    logoImage.src = `${this._iframeUrl}/assets/acimaLeasingLogo.png`
    logoImage.style.display = 'block'
    logoImage.style.margin = 'auto'
    logoContainer.appendChild(logoImage)
    return logoContainer
  }

  headline(): HTMLElement {
    let headlineContent = document.createElement('div')
    headlineContent.textContent = 'Get the stuff you need-all without credit.*'
    headlineContent.style.fontSize = '45px'
    headlineContent.style.fontWeight = '800'
    headlineContent.style.textAlign = 'center'
    headlineContent.style.margin = '40px auto'
    headlineContent.style.maxWidth = '600px'
    headlineContent.style.lineHeight = '1'
    headlineContent.style.color = darkBlue
    return headlineContent
  }

  byline(): HTMLElement {
    const bylineContent = document.createElement('div')
    bylineContent.textContent = 'Our flexible, lease-to-own solution provides you with instant shopping power.'
    bylineContent.style.textAlign = 'center'
    bylineContent.style.color = darkGray
    bylineContent.style.fontSize = '20px'
    bylineContent.style.marginTop = '40px'

    return bylineContent
  }

  leasingExplanation(): HTMLElement {
    const happyApplicantImg = document.createElement('img')
    happyApplicantImg.setAttribute('id', 'happy-applicant-img')
    happyApplicantImg.src = `${this._iframeUrl}/assets/happyCustomer.jpg`
    if (this.smallDevice() == true) {
      happyApplicantImg.style.margin = 'auto'
    }

    const cp1 = this.labelWithCheck('Lease $300 to $4,000 worth of eligible merchandise, such as furniture, electronics, and more.')
    const cp2 = this.labelWithCheck('Just meet some basic income requirements and get an instant approval decision.')
    const cp3 = this.labelWithCheck('Make payments until you own it or purchase early at a discount-either way it\'s yours to keep.')
    const checkPoints = document.createElement('div')
    checkPoints.setAttribute('id', 'check-points')
    checkPoints.style.display = 'flex'
    checkPoints.style.flexDirection = 'column'
    checkPoints.style.justifyContent = 'space-between'
    if (this.smallDevice() == true) {
      checkPoints.style.width = 'fit-content'
      checkPoints.style.marginTop = '30px'
    } else {
      checkPoints.style.width = '320px'
    }

    checkPoints.appendChild(cp1)
    checkPoints.appendChild(cp2)
    checkPoints.appendChild(cp3)

    const explanationContainer = document.createElement('div')
    explanationContainer.setAttribute('id', 'explanation-container')
    explanationContainer.style.justifyContent = 'space-between'
    explanationContainer.style.width = '85%'
    explanationContainer.style.margin = '40px auto'
    explanationContainer.appendChild(happyApplicantImg)
    explanationContainer.appendChild(checkPoints)
    if (this.smallDevice() == true) {
      explanationContainer.style.flexDirection = 'column'
    } else {
      explanationContainer.style.flexDirection = 'row'
    }

    return explanationContainer
  }


  labelWithCheck(str: string): HTMLElement {
    let cp = document.createElement('div')
    cp.style.display = 'flex'
    cp.style.flexDirection = 'row'

    let ckIconContainer = document.createElement('div')
    ckIconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="ionicon s-ion-icon" viewBox="0 0 512 512"><title>Checkmark Circle</title><path d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm-38 312.38l-80.6-89.57 23.79-21.41 56 62.22L350 153.46 374.54 174z" fill="rgb(247, 82, 0)"></path></svg>`
    ckIconContainer.style.fontSize = '25px';
    ckIconContainer.style.margin = '-3 20px 0 0'
    ckIconContainer.style.width = '35px'
    ckIconContainer.style.height = '35px'


    const sp = document.createElement('div')
    sp.textContent = str
    sp.style.color = darkGray
    sp.style.flexBasis = "0"
    sp.style.flexGrow = "1"

    cp.appendChild(ckIconContainer)
    cp.appendChild(sp)

    return cp
  }


  callToAction(): HTMLElement {
    const applyLabel = document.createElement('div')
    applyLabel.textContent = 'Get approved first'
    applyLabel.style.fontSize = '16px'
    applyLabel.style.fontWeight = 'bold'
    applyLabel.style.textAlign = 'center'
    applyLabel.style.color = darkBlue

    const applyButton = document.createElement('div')
    applyButton.textContent = 'Apply Now'
    applyButton.style.borderRadius = '30px'
    applyButton.style.textAlign = 'center'
    applyButton.style.backgroundColor = orange
    applyButton.style.color = 'fff'
    applyButton.style.fontWeight = '600'
    applyButton.style.width = '120px'
    applyButton.style.height = '32px'
    applyButton.style.lineHeight = '32px'
    applyButton.style.cursor = 'pointer'
    applyButton.style.margin = 'auto'
    applyButton.addEventListener('click', () => { this.hideModal(); this.preapproval() })

    const applyBlock = document.createElement('div')
    applyBlock.style.display = 'flex'
    applyBlock.style.flexDirection = 'column'
    applyBlock.style.justifyContent = 'center'
    applyBlock.appendChild(applyLabel)
    applyBlock.appendChild(applyButton)

    const upper = document.createElement('div')
    upper.style.width = '1px'
    upper.style.backgroundColor = lightGray
    upper.style.height = '24px'
    upper.style.margin = '0 auto 0 auto'


    const or = document.createElement('div')
    or.textContent = 'OR'
    or.style.color = darkGray
    or.style.margin = '0 auto 0 auto'


    const lower = document.createElement('div')
    lower.style.width = '1px'
    lower.style.backgroundColor = lightGray
    lower.style.height = '24px'
    lower.style.margin = '0 auto 0 auto'


    const separator = document.createElement('div')
    separator.style.display = 'flex'
    separator.style.flexDirection = 'column'
    separator.style.justifyContent = 'center'
    separator.appendChild(upper)
    separator.appendChild(or)
    separator.appendChild(lower)

    const lookForUs = document.createElement('div')
    lookForUs.textContent = 'Look for us at checkout'
    lookForUs.style.fontSize = '16px'
    lookForUs.style.fontWeight = 'bold'
    lookForUs.style.textAlign = 'center'
    lookForUs.style.color = darkBlue

    const logoImg = document.createElement('img')
    logoImg.src = `${this._iframeUrl}/assets/acimaAlone.png`
    logoImg.style.margin = 'auto'
    logoImg.style.display = 'block'

    const lookForUsBlock = document.createElement('div')
    lookForUsBlock.style.display = 'flex'
    lookForUsBlock.style.flexDirection = 'column'
    lookForUsBlock.style.justifyContent = 'center'
    lookForUsBlock.appendChild(lookForUs)
    lookForUsBlock.appendChild(logoImg)

    const callToActionContainer = document.createElement('div')
    callToActionContainer.setAttribute('id', 'call-to-action')
    callToActionContainer.style.display = 'flex'
    callToActionContainer.style.justifyContent = 'space-between'
    callToActionContainer.style.maxWidth = '400px'
    callToActionContainer.style.margin = '0 auto'
    if (this.smallDevice() == true) {
      callToActionContainer.style.flexDirection = 'column'
    } else {
      callToActionContainer.style.flexDirection = 'row'
    }

    callToActionContainer.appendChild(applyBlock)
    callToActionContainer.appendChild(separator)
    callToActionContainer.appendChild(lookForUsBlock)

    return callToActionContainer
  }


  caveat(): HTMLElement {
    const finePrintContainer = document.createElement('div')
    finePrintContainer.textContent = '* "All Without Credit" means this is not a credit, loan, or financing transaction. It does not mean or imply no inquiry will be made of credit history or creditworthiness. We consider multiple data points in reviewing your application and regularly approve customers with less than perfect credit history.'
    finePrintContainer.style.color = darkGray
    finePrintContainer.style.fontSize = '13px'
    finePrintContainer.style.width = '80%'
    finePrintContainer.style.margin = '40px auto'
    finePrintContainer.style.textAlign = 'center'
    return finePrintContainer
  }

  preapproval() {
    const acima = new AcimaLauncher({
      merchantId: this._merchantId,
      iframeUrl: this._iframeUrl
    })
    return acima.preapproval({})
  }

  smallDevice(): Boolean {
    return window.innerWidth <= 550
  }
}
